/**
 * Overview KPI Card - Enhanced version for multi-retailer view
 * 
 * Shows total metric + visual breakdown by retailer.
 */

import React from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { LucideIcon, TrendingUp, TrendingDown, Minus, HelpCircle } from 'lucide-react';
import { useLocation } from 'wouter';
import { useTenant } from '../contexts/TenantContext';
import { getRetailerInfo } from '../config/retailers';
import { MiniSparkline } from './MiniSparkline';
import { Tooltip, RichTooltip } from './ui/Tooltip';
import type { RetailerBreakdownItem } from '../lib/data-service';

interface OverviewKpiCardProps {
  label: string;
  total: number;
  icon: LucideIcon;
  format: 'currency' | 'number';
  yoyDeltaText?: string;
  yoyDeltaTone?: 'success' | 'danger' | 'neutral';
  wowDeltaText?: string;
  wowDeltaTone?: 'success' | 'danger' | 'neutral';
  retailerBreakdown?: Array<{
    retailer_id: string;
    retailer_name: string;
    value: number;
  }>;
  trendData?: Array<{ week: number; value: number }>;
  trendColor?: string;
  showComparative?: boolean;
}

export function OverviewKpiCard({
  label,
  total,
  icon: Icon,
  format,
  yoyDeltaText,
  yoyDeltaTone = 'neutral',
  wowDeltaText,
  wowDeltaTone = 'neutral',
  retailerBreakdown = [],
  trendData = [],
  trendColor = '#6366f1',
  showComparative = true
}: OverviewKpiCardProps) {
  const [, setLocation] = useLocation();
  const { setSelectedRetailerId } = useTenant();

  // Calculate 4-week average and variance
  const { average, variance, variancePercent } = React.useMemo(() => {
    if (!trendData || trendData.length < 2) {
      return { average: 0, variance: 0, variancePercent: 0 };
    }
    
    // Use last 4 weeks (excluding current week which is the last item)
    const last4Weeks = trendData.slice(-5, -1); // Get 4 weeks before the current one
    const sum = last4Weeks.reduce((acc, d) => acc + d.value, 0);
    const avg = last4Weeks.length > 0 ? sum / last4Weeks.length : 0;
    const currentValue = trendData[trendData.length - 1]?.value || 0;
    const variance = currentValue - avg;
    const variancePercent = avg > 0 ? (variance / avg) * 100 : 0;
    
    return { average: avg, variance, variancePercent };
  }, [trendData]);

  const handleRetailerClick = (retailerId: string) => {
    setSelectedRetailerId(retailerId);
    setLocation('/dashboard');
  };

  // Animated number counter
  const spring = useSpring(total, { stiffness: 100, damping: 30 });
  const displayValue = useTransform(spring, (value) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  });

  React.useEffect(() => {
    spring.set(total);
  }, [total, spring]);

  const formatValue = (value: number) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const toneColors = {
    success: 'bg-green-50 text-green-700 border-green-200',
    danger: 'bg-red-50 text-red-700 border-red-200',
    neutral: 'bg-gray-50 text-gray-700 border-gray-200'
  };

  // Get trend icon based on tone
  const getTrendIcon = (tone: 'success' | 'danger' | 'neutral') => {
    if (tone === 'success') return <TrendingUp className="w-3 h-3" />;
    if (tone === 'danger') return <TrendingDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  // Get enhanced colors based on magnitude (for large changes)
  const getEnhancedToneColors = (tone: 'success' | 'danger' | 'neutral', text?: string) => {
    if (!text) return toneColors[tone];
    
    // Extract percentage value
    const match = text.match(/([+-]?\d+\.?\d*)%/);
    if (!match) return toneColors[tone];
    
    const percentage = Math.abs(parseFloat(match[1]));
    
    // Large change (>10%)
    if (percentage >= 10) {
      if (tone === 'success') return 'bg-green-100 text-green-800 border-green-300 font-bold';
      if (tone === 'danger') return 'bg-red-100 text-red-800 border-red-300 font-bold';
    }
    
    // Medium change (5-10%)
    if (percentage >= 5) {
      if (tone === 'success') return 'bg-green-75 text-green-750 border-green-250 font-semibold';
      if (tone === 'danger') return 'bg-red-75 text-red-750 border-red-250 font-semibold';
    }
    
    return toneColors[tone];
  };

  // Calculate percentages for breakdown
  const breakdownWithPercentages = retailerBreakdown
    .filter(r => r.value > 0)
    .map(r => ({
      ...r,
      percentage: total > 0 ? (r.value / total) * 100 : 0
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
      className="bg-gradient-to-br from-white to-gray-50/50 border-2 border-gray-200 rounded-xl p-3 hover:shadow-lg transition-shadow duration-300 group flex flex-col h-[380px]"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="p-1 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
        <div className="flex flex-col gap-1 items-end">
          {yoyDeltaText && (
            <Tooltip content="Year-over-year comparison with same week last year">
              <div className={`text-[10px] px-1.5 py-0.5 rounded-md border flex items-center gap-1 transition-all cursor-help ${getEnhancedToneColors(yoyDeltaTone, yoyDeltaText)}`}>
                {getTrendIcon(yoyDeltaTone)}
                <span>{yoyDeltaText}</span>
              </div>
            </Tooltip>
          )}
          {wowDeltaText && (
            <Tooltip content="Week-over-week comparison with previous week">
              <div className={`text-[10px] px-1.5 py-0.5 rounded-md border flex items-center gap-1 transition-all cursor-help ${getEnhancedToneColors(wowDeltaTone, wowDeltaText)}`}>
                {getTrendIcon(wowDeltaTone)}
                <span>{wowDeltaText}</span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Value */}
      <div className="mb-1">
        <motion.div className="text-2xl font-bold text-gray-900 tracking-tight">
          {displayValue}
        </motion.div>
        <div className="text-xs text-gray-600 font-medium flex items-center space-x-1">
          <span>{label}</span>
          <RichTooltip
            title={label}
            description={`Sum of ${label.toLowerCase()} across all active retail partners for the selected week`}
            side="right"
          >
            <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
          </RichTooltip>
        </div>
      </div>

      {/* Trend Sparkline */}
      {trendData && trendData.length > 0 && (
        <div className="mb-1">
          <MiniSparkline 
            data={trendData} 
            color={trendColor}
            height={24}
            showGradient={true}
          />
        </div>
      )}

      {/* Comparative Metric (vs 4-week average) */}
      {showComparative && average > 0 && (
        <div className="mb-1">
          <Tooltip content={`4-week average: ${formatValue(average)}. Current week is ${Math.abs(variancePercent).toFixed(1)}% ${variancePercent >= 0 ? 'higher' : 'lower'}.`}>
            <div className={`text-[10px] px-1.5 py-0.5 rounded-md inline-flex items-center space-x-1 cursor-help ${
              variancePercent > 5 ? 'bg-green-50 text-green-700' :
              variancePercent < -5 ? 'bg-red-50 text-red-700' :
              'bg-gray-50 text-gray-600'
            }`}>
              <span>{variancePercent >= 0 ? '↗' : '↘'}</span>
              <span className="font-medium">
                {Math.abs(variancePercent).toFixed(1)}% {variancePercent >= 0 ? 'above' : 'below'} 4-week avg
              </span>
            </div>
          </Tooltip>
        </div>
      )}

      {/* Retailer Breakdown */}
      <div className="mt-auto pt-2 border-t border-gray-200 flex-1">
        {breakdownWithPercentages.length > 0 && (
          <>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Contribution by Retailer
          </div>

          {/* Stacked mini bars */}
          <div className="space-y-1">
            {breakdownWithPercentages.map((item) => {
              const retailerInfo = getRetailerInfo(item.retailer_id);
              if (!retailerInfo) return null;

              return (
                <Tooltip
                  key={item.retailer_id}
                  content={`${item.retailer_name}: ${formatValue(item.value)} (${item.percentage.toFixed(0)}% of total). Click to drill down.`}
                >
                  <div 
                    className="flex items-center space-x-2 cursor-pointer group/bar hover:bg-gray-50 rounded-lg p-1 -mx-1 transition-all"
                    onClick={() => handleRetailerClick(item.retailer_id)}
                  >
                  {/* Retailer logo + name */}
                  <div className="w-24 flex items-center space-x-1.5">
                    {retailerInfo.logoPath ? (
                      <img 
                        src={retailerInfo.logoPath} 
                        alt={item.retailer_name}
                        className="w-4 h-4 object-contain rounded shadow-sm"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div 
                        className="w-2 h-2 rounded-full shadow-sm"
                        style={{ backgroundColor: retailerInfo.colors.primary }}
                      />
                    )}
                    <div className="text-[10px] font-medium text-gray-700 truncate flex-1">
                      {item.retailer_name}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="flex-1 flex items-center space-x-1.5">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden group-hover/bar:h-2 transition-all">
                      <div
                        className="h-full rounded-full transition-all duration-500 group-hover/bar:brightness-110"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: retailerInfo.colors.primary,
                        }}
                      />
                    </div>

                    {/* Percentage + value */}
                    <div className="w-20 text-right">
                      <div className="text-[10px] font-semibold text-gray-900">
                        {item.percentage.toFixed(0)}%
                      </div>
                      <div className="text-[9px] text-gray-500">
                        {formatValue(item.value)}
                      </div>
                    </div>
                  </div>
                </div>
                </Tooltip>
              );
            })}
          </div>

          {/* Total retailers count */}
          <div className="mt-3 text-xs text-gray-500">
            {breakdownWithPercentages.length} retailer{breakdownWithPercentages.length !== 1 ? 's' : ''} contributing
          </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

